import { ArrowRightOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Batch } from '../../../shared/PreWorkServiceTypes';
import { useCallback } from 'react';
import { useCreateOrViewOrEditBatch } from './CreateOrViewOrEditBatch/useCreateOrViewOrEditBatch';
import { utc } from 'moment';
import { DATE_DD_MM_YYYY } from '../../../shared/Constants';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { hasPrivilege } from '../../../shared/Utils';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;

interface BatchSummaryCardProps extends Batch {
	onEditClick: (batchId: number) => void;
	onViewClick: (batchId: number) => void;
	onViewRegisteredCandidateClick: (batch: Batch) => void;
}

export const BatchSummaryCard = (props: BatchSummaryCardProps) => {
	const {
		name,
		description,
		startDate,
		endDate,
		status,
		onEditClick,
		onViewClick,
		onViewRegisteredCandidateClick,
	} = props;
	const { addOrEditFormStatus } = useCreateOrViewOrEditBatch();
	const { loggedInUserPrivileges } = useUserSession();

	const showEditBatch = hasPrivilege(
		PrivilegeCodes.UPDATE_BATCH,
		loggedInUserPrivileges
	);

	const showEligibleCandidates = hasPrivilege(
		PrivilegeCodes.GET_ELIGIBLE_CANDIDATES_FOR_NEXT_BATCH,
		loggedInUserPrivileges
	);

	const showViewRegisteredCandidates = hasPrivilege(
		PrivilegeCodes.GET_APPLICATIONS,
		loggedInUserPrivileges
	);

	const { t } = useTranslation();

	const handleEditClick = useCallback(() => {
		addOrEditFormStatus();
		onEditClick(props.id as number);
	}, [onEditClick, props, addOrEditFormStatus]);

	const handleViewClick = useCallback(() => {
		onViewClick(props.id as number);
	}, [onViewClick, props]);

	const handleViewCandidates = useCallback(() => {
		onViewRegisteredCandidateClick(props);
	}, [props, onViewRegisteredCandidateClick]);

	return (
		<div className="flex flex-col rounded-lg border border-solid border-gray-300 bg-[rgba(232,232,233,0.4)] p-[16px] py-[24px]">
			<div className="flex flex-row gap-6 mb-4">
				<div className="flex flex-col gap-1 w-[50%]">
					<Text className="text-light-gray text-sm">{t('batchName')}</Text>
					<Text className="text-gray-650 text-sm font-bold">{name}</Text>
				</div>
				<div className="flex flex-col gap-1 w-[50%]">
					<Text className="text-light-gray text-sm">
						{t('batchDescription')}
					</Text>
					<Text className="text-gray-650 text-sm font-bold">{description}</Text>
				</div>
			</div>

			<div className="flex flex-row gap-6 mb-4">
				<div className="flex flex-col gap-1 w-[50%]">
					<Text className="text-light-gray text-sm">{t('batchStartDate')}</Text>
					<Text className="text-gray-650 text-sm font-bold">
						<CalendarOutlined />
						<span className="ml-2">
							{utc(startDate).format(DATE_DD_MM_YYYY)}
						</span>
					</Text>
				</div>
				<div className="flex flex-col gap-1 w-[50%]">
					<Text className="text-light-gray text-sm">{t('batchEndDate')}</Text>
					<Text className="text-gray-650 text-sm font-bold">
						<CalendarOutlined />
						<span className="ml-2">{utc(endDate).format(DATE_DD_MM_YYYY)}</span>
					</Text>
				</div>
			</div>

			<div className="flex flex-row gap-6 mb-4">
				<div className="flex flex-col gap-1 w-[50%]">
					<Text className="text-light-gray text-sm">{t('status')}</Text>
					<Text className="text-gray-650 text-sm font-bold">
						{status === 'ACTIVE' ? (
							<Tag className="text-sm" color="success">
								{t('active')}
							</Tag>
						) : status === 'INACTIVE' ? (
							<Tag className="text-sm" color="error">
								{t('inactive')}
							</Tag>
						) : (
							<Tag className="text-sm" color="warning">
								{t('draft')}
							</Tag>
						)}
					</Text>
				</div>
			</div>

			<span className="border-t border-gray-300 opacity-0 py-2">
				Hidden Text
			</span>

			<div className="flex flex-col gap-4 justify-center">
				<Button type="primary" className={`w-full`} onClick={handleViewClick}>
					<span>
						{t('view')} <ArrowRightOutlined />
					</span>
				</Button>

				{showEditBatch && (
					<Button
						className={`font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-full bg-transparent`}
						variant="outlined"
						onClick={handleEditClick}
					>
						<span>
							{t('editOrUpdate')} <ArrowRightOutlined />
						</span>
					</Button>
				)}
				{showEligibleCandidates && (
					<Button
						className={`font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-full bg-transparent`}
						variant="outlined"
					>
						{t('eligibleCandidates')}
					</Button>
				)}
				{showViewRegisteredCandidates && (
					<Button
						className={`font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-full bg-transparent`}
						variant="outlined"
						onClick={handleViewCandidates}
					>
						{t('viewRegisteredCandidates')}
					</Button>
				)}
			</div>
		</div>
	);
};
